import { useMutation } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { CheckCircle2, Send } from "lucide-react";
import { inquiryApi } from "../../api/inquiries";
import { ApiError } from "../../api/client";
import { Button, Icon, Input } from "../../components/ui";
import { errorMessage, useCustomerSession } from "../account/session";

/**
 * PRD screen 18: ask whether we stock something, or how fast it can arrive. A visitor gives a
 * name and a way to reply; a signed-in customer's email is filled in. Staff reply outside the
 * system, by email or phone.
 */
export function InquiryForm({ listing }: { listing?: { id: number; name: string; sku: string } }) {
  const session = useCustomerSession();
  const email = session.data?.user?.email_address ?? "";
  const [fields, setFields] = useState({ name: "", email: "", phone: "", message: "" });
  const [problems, setProblems] = useState<Record<string, string>>({});
  const send = useMutation({ mutationFn: () => inquiryApi.send({ ...fields, email: fields.email || email, product_listing_id: listing?.id }) });
  const server = send.error instanceof ApiError ? send.error.details : {};

  if (send.isSuccess) {
    return (
      <div role="status" className="flex items-start gap-3 rounded-md border border-green-200 bg-success-100 p-5">
        <Icon icon={CheckCircle2} size={22} className="mt-0.5 text-success-700" />
        <div>
          <p className="mb-1 type-body-strong text-success-700">Thank you. We have your question.</p>
          <p className="mb-0 type-body text-text-body">We'll reply by {fields.phone && !(fields.email || email) ? "phone" : "email or phone"}, usually within a working day.</p>
        </div>
      </div>
    );
  }

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const found: Record<string, string> = {};
    if (!fields.name.trim()) found.name = "Enter your name";
    if (!(fields.email || email).trim() && !fields.phone.trim()) found.email = "Give an email or a mobile number, so we can reply";
    if (!fields.message.trim()) found.message = "Write your question";
    setProblems(found);
    if (Object.keys(found).length === 0) send.mutate();
  };
  const set = (key: keyof typeof fields) => (event: { target: { value: string } }) => {
    setFields({ ...fields, [key]: event.target.value });
    setProblems(({ [key]: _, ...rest }) => rest);
  };
  const error = (key: string) => problems[key] ?? server[key]?.[0];

  return (
    <form onSubmit={submit} noValidate className="grid gap-x-5 gap-y-4 sm:grid-cols-2">
      {listing && (
        <p className="m-0 rounded-md bg-surface-sunken px-4 py-3 type-small text-text-body sm:col-span-2">
          About <span className="text-text-strong">{listing.name}</span> · <span className="type-sku">{listing.sku}</span>
        </p>
      )}
      <Input label="Your name" autoComplete="name" value={fields.name} onChange={set("name")} error={error("name")} className="sm:col-span-2" />
      <Input label="Email" type="email" autoComplete="email" placeholder={email || undefined} value={fields.email} onChange={set("email")} error={error("email")} />
      <Input label="Mobile number" mono inputMode="tel" autoComplete="tel" placeholder="0917 000 0000" value={fields.phone} onChange={set("phone")} error={error("phone")} hint="An email or a number is enough" />
      <label className="block sm:col-span-2">
        <span className="mb-2 block type-label text-text-strong">Your question</span>
        <textarea
          value={fields.message}
          onChange={set("message")}
          rows={4}
          maxLength={2000}
          placeholder={listing ? "For example: do you have 3 of these, and how soon can they reach Kabankalan?" : "For example: do you stock a head gasket for a Yanmar TF105?"}
          className={`block w-full resize-y rounded-sm border bg-surface-card px-3 py-2 type-body inset-shadow-default focus:border-green-700 focus:outline-none ${error("message") ? "border-danger-500" : "border-border-default"}`}
        />
        {error("message") && <span role="alert" className="mt-2 block type-small text-danger-700">{error("message")}</span>}
      </label>
      {send.error && Object.keys(server).length === 0 && (
        <p role="alert" className="m-0 type-small text-danger-700 sm:col-span-2">
          {errorMessage(send.error)}
        </p>
      )}
      <div className="sm:col-span-2">
        <Button type="submit" icon={Send} disabled={send.isPending}>
          {send.isPending ? "Sending…" : "Send question"}
        </Button>
      </div>
    </form>
  );
}
